import { useRef, useState, useEffect } from 'react';
import { fetchFeaturedJobs } from '../../../lib/wordpress';

// トップページに固定表示する求人ID
const FEATURED_JOB_IDS = [313, 316, 318];

interface FeaturedJob {
  id: number;
  title: string;
  location: string;
  salary: string;
  imageUrl: string;
}

export default function FeaturedJobsSection() {
  const [jobs, setJobs] = useState<FeaturedJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadJobs() {
      try {
        const data = await fetchFeaturedJobs(FEATURED_JOB_IDS);
        setJobs(data);
      } catch (error) {
        console.error('求人データの読み込みに失敗しました:', error);
      } finally {
        setLoading(false);
      }
    }
    loadJobs();
  }, []);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleJobClick = (jobId: number) => {
    window.REACT_APP_NAVIGATE(`/job/${jobId}`);
  };

  const handleDotClick = (index: number) => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: index * containerWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.offsetWidth;
      const newIndex =
        direction === 'left'
          ? Math.max(0, activeIndex - 1)
          : Math.min(jobs.length - 1, activeIndex + 1);

      scrollContainerRef.current.scrollTo({
        left: newIndex * containerWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const containerWidth = scrollContainerRef.current.offsetWidth;
        const scrollLeft = scrollContainerRef.current.scrollLeft;
        const maxScroll = scrollContainerRef.current.scrollWidth - containerWidth;
        const index = Math.round(scrollLeft / containerWidth);

        setActiveIndex(Math.max(0, Math.min(index, jobs.length - 1)));
        setCanScrollLeft(scrollLeft > 10);
        setCanScrollRight(scrollLeft < maxScroll - 10);
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [jobs.length]);

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">求人情報を読み込み中...</p>
          </div>
        </div>
      </section>
    );
  }

  if (jobs.length === 0) {
    return (
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-500">
            <p>現在表示できる求人がありません。</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">注目の求人</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            あなたにぴったりの職場がここにあります
          </p>
        </div>

        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            <div className="flex" style={{ width: `${jobs.length * 100}%` }}>
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex justify-center items-center snap-center"
                  style={{ width: `${100 / jobs.length}%` }}
                >
                  <div
                    onClick={() => handleJobClick(job.id)}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 relative"
                    style={{ width: '800px', maxWidth: '90vw' }}
                  >
                    {/* 左矢印 */}
                    {canScrollLeft && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArrowClick('left');
                        }}
                        className="absolute left-2 top-[calc(50%-60px)] -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-12 h-12 flex items-center justify-center cursor-pointer"
                        aria-label="前へ"
                      >
                        <i className="ri-arrow-left-s-line text-3xl text-blue-500"></i>
                      </button>
                    )}

                    {/* 右矢印 */}
                    {canScrollRight && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArrowClick('right');
                        }}
                        className="absolute right-2 top-[calc(50%-60px)] -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full shadow-xl shadow-gray-400/50 transition-all w-12 h-12 flex items-center justify-center cursor-pointer"
                        aria-label="次へ"
                      >
                        <i className="ri-arrow-right-s-line text-3xl text-blue-500"></i>
                      </button>
                    )}

                    <div className="relative">
                      <div className="w-full bg-gray-100 overflow-hidden">
                        {job.imageUrl ? (
                          <img
                            src={job.imageUrl}
                            alt={`${job.title}の求人`}
                            className="w-full h-auto"
                          />
                        ) : (
                          <div className="w-full h-64 flex items-center justify-center text-gray-400">
                            <div className="text-center">
                              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                                <i className="ri-image-line text-4xl"></i>
                              </div>
                              <p className="text-sm">画像なし</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center text-gray-700 mb-3">
                        <div className="w-5 h-5 flex items-center justify-center mr-2">
                          <i className="ri-map-pin-line text-lg"></i>
                        </div>
                        <span className="text-base font-medium">{job.location}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-money-yen-circle-fill text-yellow-400 text-xl"></i>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">時給</div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-base font-bold text-yellow-500">¥</span>
                            <div className="text-lg font-bold text-gray-900 whitespace-pre-line">
                              {job.salary}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {jobs.length > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              {jobs.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`transition-all cursor-pointer shadow-md ${
                    activeIndex === index
                      ? 'w-14 h-5 bg-blue-500 rounded-full shadow-blue-300'
                      : 'w-5 h-5 bg-gray-300 rounded-full hover:bg-blue-300'
                  }`}
                  aria-label={`求人 ${index + 1} に移動`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
